/**
 * Area `wizard` of the console gateway — what only the new-agent wizard reads (customer-portal
 * review #127, fix round D, 2026-09-25).
 *
 * `GET /v1/languages/suggestion` answers the account's country and, when that country's main
 * language is finished, its code. The wizard opens its language step on that language; with
 * `null` it opens with nothing chosen and asks. A suggestion, never a fallback.
 *
 * The register is EMPTY: the operation is served.
 *
 * A VOICE PER LANGUAGE (plan-015 G1U, gap G1, ADR 0042). What the wizard collects gains `voiceByLanguage` — language
 * code → the voice chosen for it — which `POST /v1/agents` takes as `voice_by_language`. The API takes that map or
 * `voice_id` alone (only when that voice speaks every chosen language), never both: `createVoiceAgentBodyVoiceOf`
 * is that choice, so the request names the voices exactly once.
 */
import { assertOk, type WeTalkApiClient } from "@wetalk/api-client";

import type { ConsoleGateway, VoiceAgentDraft } from "../gateway.js";
import { readRecord, readText } from "../decode.js";
import type { GatewayGap } from "./slice-context.js";

export interface LanguageSuggestionView {
  /** The account's country, ISO 3166-1 alpha-2, as given at sign-up. */
  readonly country: string;
  /** Its main language when that pack is complete; `null` when there is nothing to suggest. */
  readonly languageCode: string | null;
}

declare module "../gateway.js" {
  interface ConsoleGateway {
    /** The language a new agent's wizard opens on, from the account's country. */
    getLanguageSuggestion(): Promise<LanguageSuggestionView>;
  }
  interface VoiceAgentDraft {
    /**
     * Language code → the voice that speaks it, one per chosen language (plan-015 G1). The wizard always states it;
     * left out, `voiceId` alone is sent, which the API accepts only when that voice speaks every chosen language.
     */
    readonly voiceByLanguage?: Readonly<Record<string, string>>;
  }
}

/** How a draft names its voices on `POST /v1/agents`: the per-language map when it has one, else `voice_id`. */
export function createVoiceAgentBodyVoiceOf(
  draft: Pick<VoiceAgentDraft, "voiceId" | "voiceByLanguage">,
): { readonly voice_by_language: Record<string, string> } | { readonly voice_id: string } {
  return draft.voiceByLanguage === undefined ? { voice_id: draft.voiceId } : { voice_by_language: { ...draft.voiceByLanguage } };
}

export const wizardGap: readonly GatewayGap[] = [];

export function languageSuggestionOf(body: unknown): LanguageSuggestionView {
  const data = readRecord(readRecord(body, "getLanguageSuggestion")["data"], "getLanguageSuggestion.data");
  const languageCode = data["language_code"];
  return {
    country: readText(data["country"], "getLanguageSuggestion.data.country"),
    languageCode:
      languageCode === null ? null : readText(languageCode, "getLanguageSuggestion.data.language_code"),
  };
}

export function wizardApiSlice(client: WeTalkApiClient): Pick<ConsoleGateway, "getLanguageSuggestion"> {
  return {
    async getLanguageSuggestion(): Promise<LanguageSuggestionView> {
      return languageSuggestionOf(assertOk(await client.getLanguageSuggestion()));
    },
  };
}
